import { NoDataEmptyState } from '@carbon/ibm-products';
import { Button, Dropdown, InlineLoading, Search } from '@carbon/react';
import {
  Add,
  CheckmarkOutline,
  Edit,
  Map as MapIcon,
  TrashCan,
  Upload,
  WarningAlt,
  WarningHex,
} from '@carbon/react/icons';
import { DataTable, DataTableColumnDef } from 'components/data-table';
import { FloatingTooltip } from 'components/floating-tooltip/floating-tooltip';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useMemo } from 'react';
import { formatNumber } from 'utils/number';
import {
  batchDefaultAtom,
  curationRowsAtom,
  filteredRowsAtom,
  focusMapRowAtom,
  isRowValid,
  removeRowAtom,
  tableSearchAtom,
  validityCountsAtom,
  validityFilterAtom,
} from './atoms';
import styles from './bulk-upload.module.scss';
import {
  EM_DASH,
  FIELD_LABELS,
  TABLE_FIELDS,
  displayValue,
  errorLines,
  isBlank,
} from './fields';
import { OUTCOME_LABELS } from './batch';
import { curationMapRow, mapBlocker } from './map';
import { CreatingSpinner } from './results-table';
import type { BatchDefault, CurationRow, ValidityFilter } from './types';

const count = (value: number) => formatNumber({ value, minPrecision: 0 });

/**
 * The CRM Account a row resolves to: its own account, else the account linked
 * to its own entity (as the server resolved it), else the batch default.
 * @param {CurationRow} row - The row
 * @param {BatchDefault} batchDefault - The batch default
 * @return {string} Display text
 */
const accountText = (row: CurationRow, batchDefault: BatchDefault) => {
  if (!isBlank(row.values.crmAccount)) {
    return displayValue(row.values.crmAccount);
  }
  if (!isBlank(row.values.insightsEntity)) {
    return row.result?.accountId ?? EM_DASH;
  }
  return batchDefault.accountName ?? (batchDefault.accountId || EM_DASH);
};

const ValidityCell = ({ row }: { row: CurationRow }) => {
  if (row.pending) {
    return <InlineLoading description="Validating" />;
  }
  const errors = errorLines(row.result?.errors ?? {});
  const warnings = [
    ...(row.result?.warnings ?? []),
    ...Object.values(row.ignored),
  ];
  const lines = [...errors, ...warnings];
  const tooltip = lines.length ? (
    <ul className={styles.validityTooltip}>
      {lines.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  ) : null;

  if (errors.length || !row.result) {
    return (
      <FloatingTooltip content={tooltip}>
        <span className={styles.statusInvalid} tabIndex={0}>
          <WarningHex />
          Invalid
        </span>
      </FloatingTooltip>
    );
  }
  return (
    <span className={styles.statusValid}>
      <CheckmarkOutline />
      Valid
      {warnings.length > 0 && (
        <FloatingTooltip content={tooltip}>
          <span className={styles.statusWarning} tabIndex={0}>
            <WarningAlt aria-label="Warnings" />
          </span>
        </FloatingTooltip>
      )}
    </span>
  );
};

const CreationStatusCell = ({ row }: { row: CurationRow }) => {
  if (!isRowValid(row)) return <span>{EM_DASH}</span>;
  // As the results page shows it, so the spinner stays put when it loads.
  return (
    <div className={styles.outcomeLine}>
      <span>{OUTCOME_LABELS.creating}</span>
      <CreatingSpinner />
    </div>
  );
};

type CurationTableProps =
  | {
      mode: 'edit';
      onAdd: () => void;
      onUpload: () => void;
      onEdit: (row: CurationRow) => void;
    }
  /** Read-only while the submit is in flight. */
  | { mode: 'creating' };

/**
 * The working table: every curated row with its validity, searchable and
 * filterable by validity, with Edit, View asset on map and Remove per row.
 * Shows the empty state until the first row arrives. While creating it is
 * read-only: no add, upload, edit or remove.
 * @param {CurationTableProps} props - The mode, and in edit its handlers
 * @return {JSX.Element} The table or its empty state
 */
export const CurationTable = (props: CurationTableProps) => {
  const handlers = props.mode === 'edit' ? props : null;
  const onEdit = handlers?.onEdit;
  const creating = props.mode === 'creating';
  const rows = useAtomValue(curationRowsAtom);
  const filteredRows = useAtomValue(filteredRowsAtom);
  const counts = useAtomValue(validityCountsAtom);
  const batchDefault = useAtomValue(batchDefaultAtom);
  const [search, setSearch] = useAtom(tableSearchAtom);
  const [validityFilter, setValidityFilter] = useAtom(validityFilterAtom);
  const removeRow = useSetAtom(removeRowAtom);
  const focusMapRow = useSetAtom(focusMapRowAtom);

  const columns = useMemo<DataTableColumnDef<CurationRow>[]>(
    () => [
      {
        id: 'validity',
        header: 'Validity',
        size: 120,
        cell: ({ row }) => <ValidityCell row={row.original} />,
        meta: { overflowVisible: true },
      },
      ...(creating
        ? [
            {
              id: 'creationStatus',
              header: 'Creation status',
              size: 180,
              cell: ({ row }) => <CreationStatusCell row={row.original} />,
            } as DataTableColumnDef<CurationRow>,
          ]
        : []),
      ...TABLE_FIELDS.map((key): DataTableColumnDef<CurationRow> => ({
        id: key,
        header: FIELD_LABELS[key],
        size: key === 'tractFullLegalDescription' ? 280 : 150,
        accessorFn: (row) => {
          if (key === 'crmAccount') return accountText(row, batchDefault);
          return displayValue(row.values[key]);
        },
      })),
      {
        id: 'actions',
        header: 'Actions',
        size: onEdit ? 144 : 64,
        meta: { overflowVisible: true },
        cell: ({ row }) => {
          const mapBlocked = mapBlocker(curationMapRow(row.original));
          return (
            // Its buttons act on their own; only the rest of the row opens the map.
            <div
              className={styles.rowActions}
              onClick={(event) => event.stopPropagation()}
            >
              {onEdit && (
                <Button
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  renderIcon={Edit}
                  iconDescription="Edit asset"
                  tooltipPosition="left"
                  onClick={() => onEdit(row.original)}
                />
              )}
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={MapIcon}
                iconDescription={mapBlocked ?? 'View asset on map'}
                tooltipPosition="left"
                disabled={!!mapBlocked}
                onClick={() => focusMapRow(row.original.clientId)}
              />
              {onEdit && (
                <Button
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  renderIcon={TrashCan}
                  iconDescription="Remove asset"
                  tooltipPosition="left"
                  onClick={() => removeRow(row.original.clientId)}
                />
              )}
            </div>
          );
        },
      },
    ],
    [batchDefault, creating, focusMapRow, onEdit, removeRow]
  );

  const filterItems: { id: ValidityFilter; text: string }[] = [
    { id: 'all', text: `All (${count(counts.all)})` },
    { id: 'valid', text: `Valid (${count(counts.valid)})` },
    { id: 'invalid', text: `Invalid (${count(counts.invalid)})` },
  ];

  if (rows.length === 0 && handlers) {
    return (
      <div className={styles.emptyState}>
        <NoDataEmptyState
          title="Start by adding assets"
          subtitle="Upload a CSV or Excel file, or add assets one at a time."
          size="lg"
        />
        <div className={styles.emptyStateActions}>
          <Button kind="tertiary" renderIcon={Add} onClick={handlers.onAdd}>
            Add asset
          </Button>
          <Button
            kind="primary"
            renderIcon={Upload}
            onClick={handlers.onUpload}
          >
            Upload File
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tableWrapper}>
      <DataTable<CurationRow>
        data={filteredRows}
        columns={columns}
        getRowId={(row) => row.clientId}
        onSelectionChange={({ selection }) => {
          const row = filteredRows.find(
            (candidate) => candidate.clientId === selection.toggledNodes[0]
          );
          if (row && !mapBlocker(curationMapRow(row))) {
            focusMapRow(row.clientId);
          }
        }}
        initialTableState={{
          columnPinning: {
            left: ['validity', 'creationStatus'],
            right: ['actions'],
          },
        }}
        config={{
          globalSearch: false,
          columnFilters: false,
          sorting: false,
          columnReordering: false,
          columnVisibility: false,
        }}
        fillLeftToolbarActions
        leftToolbarActions={[
          <Search
            key="search"
            size="lg"
            labelText="Search assets"
            placeholder="Search assets"
            value={search}
            onChange={(event) => setSearch(event.target.value ?? '')}
            className={styles.tableSearch}
          />,
          <Dropdown
            key="validity"
            id="bulk-upload-validity-filter"
            size="lg"
            titleText="Validity filter"
            hideLabel
            label="Filter by validity"
            className={styles.validityFilter}
            items={filterItems}
            selectedItem={filterItems.find(
              (item) => item.id === validityFilter
            )}
            itemToString={(item) => item?.text ?? ''}
            onChange={({ selectedItem }) =>
              setValidityFilter(selectedItem?.id ?? 'all')
            }
          />,
        ]}
        rightToolbarActions={
          handlers
            ? [
                <Button
                  key="add"
                  kind="ghost"
                  hasIconOnly
                  renderIcon={Add}
                  iconDescription="Add asset"
                  tooltipPosition="bottom"
                  tooltipAlignment="end"
                  onClick={handlers.onAdd}
                />,
                <Button
                  key="upload"
                  kind="primary"
                  renderIcon={Upload}
                  onClick={handlers.onUpload}
                >
                  Upload File
                </Button>,
              ]
            : undefined
        }
      />
    </div>
  );
};
